// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsProvider
 * @package @geenius/analytics/react-css
 * @description Defines the provider boundary for the React CSS analytics
 * variant. The provider creates a tracker instance and exposes it to hooks and
 * components rendered within the subtree.
 */

import type {
  AnalyticsStorageAdapter,
  ConsentSettings,
  TrackerConfigInput,
  TrackerInstance,
} from "@geenius/analytics";
import { AnalyticsContextError, createTracker } from "@geenius/analytics";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type AnalyticsCopyTranslator = (
  key: string,
  params?: Record<string, string | number>,
) => string;

/**
 * Props accepted by {@link AnalyticsProvider}.
 *
 * @property config Tracker configuration used to create the provider instance.
 * @property translator Optional app-level copy translator used by analytics UI helpers.
 * @property children Descendant React nodes that consume analytics context.
 * @property initialConsent Optional initial consent settings to apply on mount.
 * @property storage Optional self-hosted storage adapter used before outbound provider sync.
 */
export interface AnalyticsProviderProps {
  config: TrackerConfigInput;
  children: React.ReactNode;
  translator?: AnalyticsCopyTranslator;
  initialConsent?: ConsentSettings | null;
  storage?: AnalyticsStorageAdapter | AnalyticsStorageAdapter[];
}

export interface AnalyticsConsentContextValue {
  consentSettings: ConsentSettings | null;
  updateConsent: (settings: ConsentSettings) => void;
}

/**
 * Options for {@link useAnalyticsContext}.
 *
 * @property optional When true, returns `null` instead of throwing outside a provider.
 */
export interface UseAnalyticsContextOptions {
  optional?: boolean;
}

/**
 * React context carrying the active analytics tracker for the CSS variant.
 */
export const AnalyticsContext = createContext<TrackerInstance | null>(null);

/**
 * React context carrying an optional host-app copy translator for analytics UI.
 */
export const AnalyticsCopyContext =
  createContext<AnalyticsCopyTranslator | null>(null);

export const AnalyticsConsentContext =
  createContext<AnalyticsConsentContextValue | null>(null);

/**
 * Reads the current analytics tracker from context.
 *
 * @param options Controls whether a missing provider should throw or return `null`.
 * @returns The active tracker instance, or `null` when `optional` is enabled.
 * @throws {AnalyticsContextError} When called outside a provider without `optional`.
 */
export function useAnalyticsContext(
  options: UseAnalyticsContextOptions = {},
): TrackerInstance | null {
  const context = useContext(AnalyticsContext);

  if (!context && !options.optional) {
    throw new AnalyticsContextError({
      message:
        "useAnalyticsContext must be called inside an <AnalyticsProvider> tree.",
    });
  }

  return context;
}

export function useAnalyticsCopyTranslator(): AnalyticsCopyTranslator | null {
  return useContext(AnalyticsCopyContext);
}

export function useAnalyticsConsent(): AnalyticsConsentContextValue | null {
  return useContext(AnalyticsConsentContext);
}

/**
 * Provides a tracker instance to the React CSS subtree.
 *
 * @param props Provider configuration and descendant content.
 * @returns A context provider wrapping the supplied children.
 */
export function AnalyticsProvider(
  props: AnalyticsProviderProps,
): React.ReactElement {
  const tracker = useMemo(
    () =>
      createTracker(
        props.storage
          ? { ...props.config, storage: props.storage }
          : props.config,
      ),
    [props.config, props.storage],
  );
  const [consentSettings, setConsentSettings] =
    useState<ConsentSettings | null>(props.initialConsent ?? null);
  useEffect(() => {
    if (props.initialConsent) {
      setConsentSettings(props.initialConsent);
      tracker.consent(props.initialConsent);
    }
  }, [props.initialConsent, tracker]);
  const updateConsent = useCallback(
    (settings: ConsentSettings): void => {
      setConsentSettings(settings);
      void tracker.consent(settings);
    },
    [tracker],
  );

  return (
    <AnalyticsContext.Provider value={tracker}>
      <AnalyticsConsentContext.Provider
        value={{ consentSettings, updateConsent }}
      >
        <AnalyticsCopyContext.Provider value={props.translator ?? null}>
          {props.children}
        </AnalyticsCopyContext.Provider>
      </AnalyticsConsentContext.Provider>
    </AnalyticsContext.Provider>
  );
}
